import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaCalendarAlt as FaCalendarAltRaw,
  FaClock as FaClockRaw,
  FaRocket as FaRocketRaw,
  FaSmile as FaSmileRaw,
  FaStream as FaStreamRaw,
} from 'react-icons/fa';
import {
  useCreateMarketingCampaign,
  useMarketingSubscriptionChannels,
  useMarketingTagSets,
  useMarketingTemplates,
} from '../hooks/useMarketing';
import {
  PrimaryButton,
  SecondaryButtonLink,
} from '../styles/marketingSharedStyles';
import { supabase } from '../services/supabase';
import {
  BackLink,
  BtnRow,
  Card,
  CharCount,
  DateTimeRow,
  EncodingRow,
  Field,
  InputWithIcon,
  Label,
  Layout,
  Main,
  MessageTextarea,
  PageSub,
  PageTitle,
  PageWrap,
  PhoneBack,
  PhoneBubble,
  PhoneHeader,
  PhoneInputLabel,
  PhoneMock,
  PhoneScreen,
  PhoneTime,
  RadioDesc,
  RadioGroup,
  RadioInput,
  RadioOption,
  Row,
  SectionHead,
  SectionTitle,
  SectionTitleRow,
  Select,
  Sidebar,
  SidebarCard,
  SidebarTitle,
  StepNum,
  TagChip,
  TagChips,
  TextInput,
  TopBar,
} from '../components/marketing/smsBlastPageLayout';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCalendarAlt: any = FaCalendarAltRaw;
const FaClock: any = FaClockRaw;
const FaRocket: any = FaRocketRaw;
const FaSmile: any = FaSmileRaw;
const FaStream: any = FaStreamRaw;

const PERSONALIZATION_TAGS = ['{{first_name}}', '{{birthday}}', '{{city}}'];
const SMS_CHAR_LIMIT = 160;

const normalizePhoneNumber = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const normalized = trimmed.startsWith('+')
    ? `+${trimmed.slice(1).replace(/\D/g, '')}`
    : `+${trimmed.replace(/\D/g, '')}`;
  return /^\+[1-9]\d{6,14}$/.test(normalized) ? normalized : null;
};

const parseRecipientPhones = (raw: string): string[] => {
  const tokens = raw
    .split(/[\n,;]/)
    .map((token) => normalizePhoneNumber(token))
    .filter((value): value is string => Boolean(value));
  return Array.from(new Set(tokens));
};

export default function NewMessagePage() {
  const navigate = useNavigate();
  const tagSets = useMarketingTagSets();
  const subscriptionChannels = useMarketingSubscriptionChannels();
  const templates = useMarketingTemplates();
  const createCampaign = useCreateMarketingCampaign();

  const [campaignName, setCampaignName] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [recipientPhonesInput, setRecipientPhonesInput] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sendNow, setSendNow] = useState(true);
  const [scheduleDate, setScheduleDate] = useState('2024-10-24');
  const [scheduleTime, setScheduleTime] = useState('09:00');

  const segmentOptions = useMemo(
    () => (tagSets.data ?? []).map((t) => ({ value: t.id, label: t.name })),
    [tagSets.data]
  );
  const listOptions = useMemo(
    () => (subscriptionChannels.data ?? []).map((channel) => ({ value: channel.id, label: channel.name })),
    [subscriptionChannels.data]
  );
  const smsTemplates = useMemo(
    () => (templates.data ?? []).filter((t) => t.channel === 'sms'),
    [templates.data]
  );

  const charCount = messageBody.length;
  const previewText = messageBody.trim() || 'Hi {{first_name}}! Our Summer Weekend Sale is live. Use code SUMMER20 for 20% off site-wide!';
  const recipientPhones = useMemo(
    () => parseRecipientPhones(recipientPhonesInput),
    [recipientPhonesInput]
  );

  const insertTag = (tag: string) => {
    setMessageBody((prev) => prev + tag);
  };

  const applyTemplate = () => {
    const first = smsTemplates[0];
    if (first?.body) setMessageBody(first.body);
  };

  const handleLaunch = async () => {
    if (!campaignName.trim()) {
      window.alert('Please enter a message name.');
      return;
    }
    if (!messageBody.trim()) {
      window.alert('Please enter a message.');
      return;
    }
    if (sendNow && recipientPhones.length === 0) {
      window.alert('Add at least one valid recipient phone in E.164 format (for example: +15551234567).');
      return;
    }
    try {
      const scheduledAt = sendNow
        ? null
        : new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      const campaign = await createCampaign.mutateAsync({
        name: campaignName.trim(),
        channel: 'sms',
        status: 'scheduled',
        scheduledAt: scheduledAt ?? undefined,
        metadata: {
          body: messageBody.trim(),
          segmentId: segmentId || null,
          recipientPhones,
          encoding: 'GSM7',
        },
      });
      if (sendNow) {
        const { data, error } = await supabase.functions.invoke('launch-marketing-campaign', {
          body: { campaign_id: campaign.id },
        });
        if (error) {
          throw new Error(error.message || 'Failed to launch message.');
        }
        if (data && typeof data === 'object' && 'error' in data && (data as { error?: string }).error) {
          throw new Error((data as { error: string }).error);
        }
      }
      navigate('/marketing');
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to create message.');
    }
  };

  return (
    <PageWrap>
      <TopBar>
        <BackLink to="/marketing">← Back to Marketing</BackLink>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <SecondaryButtonLink to="/marketing/keywords">
            <FaStream size={14} /> Design SMS Funnel
          </SecondaryButtonLink>
          <PrimaryButton
          type="button"
          onClick={handleLaunch}
            disabled={createCampaign.isPending || !campaignName.trim() || !messageBody.trim()}
          >
            <FaRocket size={14} /> {createCampaign.isPending ? 'Launching…' : 'Launch Message'}
          </PrimaryButton>
        </div>
      </TopBar>

      <Layout>
        <Main>
          <div>
            <PageTitle>Create SMS Blast</PageTitle>
            <PageSub>
              Compose, segment, and schedule your message for maximum impact.
            </PageSub>
          </div>

          <Card>
            <SectionHead>
              <SectionTitleRow>
                <StepNum>1</StepNum>
                <SectionTitle>Message Details</SectionTitle>
              </SectionTitleRow>
            </SectionHead>
            <Row>
              <Field>
                <Label>Message Name</Label>
                <TextInput
                  type="text"
                  placeholder="e.g., Summer Weekend Flash Sale"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Recipient List / Segment</Label>
                <Select
                  value={segmentId}
                  onChange={(e) => setSegmentId(e.target.value)}
                  aria-label="Select a list or segment"
                >
                  <option value="">Select a list or segment</option>
                  {listOptions.length > 0 && (
                    <optgroup label="Lists">
                      {listOptions.map((opt) => (
                        <option key={`list-${opt.value}`} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {segmentOptions.length > 0 && (
                    <optgroup label="Segments">
                      {segmentOptions.map((opt) => (
                        <option key={`segment-${opt.value}`} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </Select>
              </Field>
            </Row>
            <Field style={{ marginTop: '1rem' }}>
              <Label>Recipient Phones (for immediate sends)</Label>
              <MessageTextarea
                style={{ minHeight: '84px' }}
                placeholder="+15551234567, +15557654321"
                value={recipientPhonesInput}
                onChange={(e) => setRecipientPhonesInput(e.target.value)}
                aria-label="Recipient phone numbers"
              />
              <EncodingRow style={{ justifyContent: 'space-between' }}>
                <span>One per line or comma separated.</span>
                <CharCount>
                  Valid recipients: <span className={recipientPhones.length > 0 ? 'purple' : ''}>{recipientPhones.length}</span>
                </CharCount>
              </EncodingRow>
            </Field>
          </Card>

          <Card>
            <SectionHead>
              <SectionTitleRow>
                <StepNum>2</StepNum>
                <SectionTitle>Message Body</SectionTitle>
              </SectionTitleRow>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaSmile size={18} style={{ color: 'var(--onSurfaceVariant)' }} aria-hidden />
                <PrimaryButton type="button" onClick={applyTemplate} disabled={smsTemplates.length === 0}>
                  Use Template
                </PrimaryButton>
              </div>
            </SectionHead>
            <TagChips>
              {PERSONALIZATION_TAGS.map((tag) => (
                <TagChip key={tag} type="button" onClick={() => insertTag(tag)}>
                  {tag}
                </TagChip>
              ))}
            </TagChips>
            <MessageTextarea
              placeholder="Type your message here..."
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              aria-label="Message body"
            />
            <EncodingRow>
              <span>GSM7 Encoding</span>
              <CharCount>
                <span className={charCount > 0 ? 'purple' : ''}>{charCount}</span>/{SMS_CHAR_LIMIT}
              </CharCount>
            </EncodingRow>
          </Card>

          <Card>
            <SectionHead>
              <SectionTitleRow>
                <StepNum>3</StepNum>
                <SectionTitle>Delivery Schedule</SectionTitle>
              </SectionTitleRow>
            </SectionHead>
            <RadioGroup>
              <RadioOption>
                <RadioInput
                  type="radio"
                  name="schedule"
                  checked={sendNow}
                  onChange={() => setSendNow(true)}
                />
                <div>
                  Send Immediately
                  <RadioDesc>Message will start sending now.</RadioDesc>
                </div>
              </RadioOption>
              <RadioOption>
                <RadioInput
                  type="radio"
                  name="schedule"
                  checked={!sendNow}
                  onChange={() => setSendNow(false)}
                />
                <div>
                  Schedule for Later
                  <RadioDesc>Pick a future date and time.</RadioDesc>
                </div>
              </RadioOption>
            </RadioGroup>
            <DateTimeRow>
              <Field>
                <Label>Date</Label>
                <InputWithIcon>
                  <FaCalendarAlt size={14} />
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    disabled={sendNow}
                    aria-label="Schedule date"
                  />
                </InputWithIcon>
              </Field>
              <Field>
                <Label>Time (EST)</Label>
                <InputWithIcon>
                  <FaClock size={14} />
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    disabled={sendNow}
                    aria-label="Schedule time"
                  />
                </InputWithIcon>
              </Field>
            </DateTimeRow>
          </Card>
        </Main>

        <Sidebar>
          <SidebarCard>
            <SidebarTitle>Mobile Preview</SidebarTitle>
            <PhoneMock>
              <PhoneScreen>
                <PhoneHeader>
                  <PhoneBack>←</PhoneBack>
                  <span>SMS Blast</span>
                  <span style={{ marginLeft: 'auto' }}>ℹ</span>
                </PhoneHeader>
                <PhoneTime>TODAY 10:45 AM</PhoneTime>
                <PhoneBubble>{previewText}</PhoneBubble>
                <PhoneInputLabel>Text Message</PhoneInputLabel>
              </PhoneScreen>
            </PhoneMock>
            <BtnRow style={{ marginTop: '1rem' }}>
              
              <PrimaryButton type="button" onClick={handleLaunch} disabled={createCampaign.isPending || !campaignName.trim() || !messageBody.trim()}>
                <FaRocket size={14} /> Launch Message
              </PrimaryButton>
            </BtnRow>
          </SidebarCard>
        </Sidebar>
      </Layout>
    </PageWrap>
  );
}
